import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { decodeToken } from "react-jwt";

import {
  AuthContextData,
  AuthProviderProps,
  DataTokenJwt,
  TokensOfUser,
  User,
  IRequestAccountUser,
  IRequestUpdateAccountUser,
} from "./interfaces";
import { api } from "../services/api";
import { apiProfile } from "../services/api-profile";
import {
  deleteToken,
  setToken,
  getToken,
  getIdToken,
  setIdToken,
  deleteIdToken,
  setUsername as setUsernameStorage,
  getUsername,
  deleteUsername,
} from "../support/storage/BrowserStorage";
import {
  IChangePassword,
  IConfirmNewPassword,
  ICreateAccountProfile,
  IRequestConfirmAccount,
  IRequestSignIn,
  IRequestSignUp,
  IRequestSignUpCognitoProfile,
} from "../common/interfaces";
import { initialsName } from "../utils/initials-name";
import { getMyProperties } from "../services/property";
import { IProperty } from "../modules/properties/interfaces";
import { Box, CircularProgress } from "@mui/material";
import { useDialog } from "./useDialog";
import { useTranslate } from "./useTranslate";

const AuthContext = createContext<AuthContextData | null>(null);

export function AuthProvider({ children }: AuthProviderProps) {
  const application = "BovPilot";

  const [ready, setReady] = useState(false);
  const { translate } = useTranslate();
  const [idUserCollaborator, setIdUserCollaborator] = useState("");
  const [username, setUsername] = useState("");
  const [user, setUser] = useState<User | null>(null);
  const [tokensUser, setTokensUser] = useState<TokensOfUser>();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [propertiesIds, setPropertiesIds] = useState<string[]>([]);
  const [properties, setProperties] = useState<any[]>([]);

  const navigate = useNavigate();

  const signIn = async ({ userName, password }: IRequestSignIn) => {
    const { data, status } = await api.post("/cognito/login", {
      userName,
      password,
      versionApp: {
        app: application,
        version: "1.0.0",
        os: "web",
        versionOs: "1.0.0",
      },
    });

    if (status === 200 && !data?.permissions.includes("dashBoard"))
      throw new Error("Forbidden");

    setUsername(userName);
    setUsernameStorage(userName);
    setTokensUser({
      jwtToken: data.accessToken,
      idToken: data.idToken,
    });
    setToken(data.accessToken);
    setIdToken(data.idToken);
    setIsAuthenticated(true);
  };

  const createAccountProfile = async (data: ICreateAccountProfile) => {
    await apiProfile.post("/profiles", {
      ...data,
      attributes: [
        ...data.attributes,
        { type: "application", value: application },
      ],
    });
  };

  const confirmAccount = async ({
    confirmationCode,
    userName,
  }: IRequestConfirmAccount) => {
    await api.post("/cognito/validateCode", {
      confirmationCode,
      userName,
    });
  };

  const signUp = async ({
    userName,
    password,
    email,
    phone,
  }: IRequestSignUp) => {
    const body: IRequestSignUp = { userName, password };

    if (email) body.email = email;
    if (phone) body.phone = phone;

    await api.post("/cognito/createUserCognito", body);
    setUsername(userName!);
    setUsernameStorage(userName!);
  };

  const signUpCongnitoProfile = async ({
    userName,
    password,
    email,
    phone,
    idUser,
  }: IRequestSignUpCognitoProfile) => {
    const body: IRequestSignUpCognitoProfile = { userName, password, idUser };

    if (email) body.email = email;
    if (phone) body.phone = phone;

    await api.post("/cognito/createUserCognitoProfile", body);
    setUsername(userName!);
    setUsernameStorage(userName!);
  };

  const changePassword = useCallback(
    async ({ password, newPassword }: IChangePassword) => {
      const userNameStorage = getUsername();
      await api.post(
        "cognito/setPassword",
        {
          userName: username || userNameStorage,
          password,
          newPassword,
        },
        {
          headers: {
            Authorization: tokensUser?.idToken ?? "",
          },
        }
      );
    },
    [tokensUser?.idToken, username]
  );

  const verifyUsername = async (value: string) => {
    await api.post("/cognito/forgotPassword", { userName: value });
  };

  const confirmNewPassword = async ({
    value,
    code,
    password,
  }: IConfirmNewPassword) => {
    await api.post("/cognito/confirmForgotPassword/", {
      userName: value,
      confirmationCode: code,
      password,
    });
  };

  const resendCode = async (userName: string) => {
    await api.post("/cognito/reSendCodeUserCognito", {
      userName,
    });
  };

  const createUser = async (user: IRequestAccountUser) => {
    await api.post(`/user?application=${application}`, user);
  };

  const getUserProfile = useCallback(async () => {
    const { data } = await api.get("/userIdentity");
    return data;
  }, []);

  const updateUserProfile = async ({ id, data }: IRequestUpdateAccountUser) => {
    await api.put(`/user/${id}`, data);

    const { cpf, office, name, email, phone, date_birth } = data;

    setUser(
      (oldState) =>
        ({
          ...oldState,
          cpf,
          office,
          name,
          email,
          phone,
          dob: date_birth,
        } as any)
    );
  };

  const signOut = useCallback(() => {
    deleteToken();
    deleteIdToken();
    deleteUsername();
    setIsAuthenticated(false);
    setUser(null);
    navigate("/");
  }, []);

  const dialogOption = useMemo(
    () => ({
      title: translate("global.important"),
      message: translate("property.warning_create_property"),
    }),
    [translate]
  );

  const dialog = useDialog();

  const location = useLocation();

  const getProperties = useCallback(
    async (userId: string) => {
      try {
        const properties: IProperty[] = await getMyProperties(userId);

        setProperties(properties);

        const ids = properties.map((property) => property.id);

        setPropertiesIds(ids);
      } catch {
        if (location.pathname !== "/properties/create") {
          navigate("/properties/create");
          dialog.open(dialogOption);
        }
        setPropertiesIds([]);
      }
    },
    [location.pathname, navigate, dialog, dialogOption]
  );

  const loadUser = useCallback(async () => {
    const user = await getUserProfile();
    const sigla = initialsName(user.name);

    await getProperties(user.id);
    const idToken = getIdToken();
    const token = getToken();

    setTokensUser({
      jwtToken: token!,
      idToken: idToken!,
    });

    const { value: type } = user.attributes.find(
      (attr: any) => attr.type === "property"
    );

    setUser({
      sigla,
      identityCognito: user.identity,
      id: user.id,
      name: user?.name,
      email: user?.email,
      phone: user?.phone,
      cpf: user?.cpf,
      dob: user?.date_birth,
      office: user?.office,
      type,
    });
  }, [getProperties, getUserProfile]);

  const verifyToken = useCallback(async () => {
    try {
      const token = getToken();
      const idToken = getIdToken();

      if (!token || !idToken) {
        signOut();
        return;
      }

      const { exp } = decodeToken(token) as DataTokenJwt;
      const expireDate = exp * 1000;
      const currentDate = new Date().getTime();

      if (currentDate > expireDate) {
        signOut();
        return;
      }

      await loadUser();
      setIsAuthenticated(true);
    } catch (err) {
      console.error(err);
    } finally {
      setReady(true);
    }
  }, [signOut, loadUser]);

  const authProviderProps = useMemo(
    () => ({
      user,
      loadUser,
      createUser,
      signIn,
      signUp,
      changePassword,
      resendCode,
      confirmAccount,
      confirmNewPassword,
      signOut,
      getUserProfile,
      verifyUsername,
      createAccountProfile,
      updateUserProfile,
      isAuthenticated,
      propertiesIds,
      idUserCollaborator,
      setIdUserCollaborator,
      signUpCongnitoProfile,
      application,
      properties,
    }),
    [
      changePassword,
      getUserProfile,
      idUserCollaborator,
      isAuthenticated,
      loadUser,
      properties,
      propertiesIds,
      signOut,
      user,
    ]
  );

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  if (!ready)
    return (
      <Box
        width="100vw"
        height="100vh"
        position="fixed"
        display="flex"
        bgcolor="white"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );

  return (
    <AuthContext.Provider value={authProviderProps as AuthContextData}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("required auth provider");
  return context;
}
